import { genericGradeTemplate, getVesselGrade } from "@idwal/idwal-react-components";
import { Tooltip } from "primereact/tooltip";
import { getTranslationObject } from "../../services/Translation";
import { INSPECTION_STATUS_FULL_REPORT } from "../../utils/Constants";
import { RiskLevel } from "../../types/RiskLevel";
import { Tag } from "primereact/tag";
import { DefectStatus } from "../../types/DefectStatus";
import { SharedDefectItem } from "../../types/SharedDefectItem";
import { filterScoreHistoryByType, getFriendlyInspectionStatusText } from "../../utils/FleetTableDataHelper";
import {FleetDataItem} from "../../types/FleetDataItem";
import {MenuButton} from "../MenuButton/MenuButton";
import {openUrl} from "../../services/WindowOpenService";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

export const CUSTOMER_PORTAL_ROUTE = "inspection";
export const SCREENING_REPORT_ROUTE = "screening";
const t = getTranslationObject();


export const shipBodyTemplate = ( rowData: SharedDefectItem ) => { 
    let name;
    let imoNum;
    if (rowData.shipName) {
        name = rowData.shipName;
        imoNum = rowData.imo;
    }
    else {
        name = rowData.shipName
        imoNum = rowData.imo
    }

    return (
        <div className="flex justify-content-start flex-wrap">
            <div>
                <p>{name}</p>
                <small>{imoNum}</small>
            </div>
        </div>
    );
};

/**
 * Shows status tag in the status dropdown value
 * @returns
 * @param status
 */
export const sharedDefectStatusDropdownValueTemplate = (status: number) => {        
    if (status !== null && status !== undefined) {
        if (status === DefectStatus.closed) {
            return <Tag severity="success" value={t("defectsTable.defectStatusClosed")}/>
        }

        if (status === DefectStatus.inProgress) {
            return <Tag severity="info" value={t("defectsTable.defectStatusInProgress")}/>
        }

        if (status === DefectStatus.rectified) {
            return <Tag severity="warning" value={t("defectsTable.defectStatusRectified")}/>
        }

        if (status === DefectStatus.open) {
            return <Tag severity="danger" value={t("defectsTable.defectStatusOpen")}/>
        }
    }  

    return <Tag severity="danger" value={t("defectsTable.defectStatusOpen")}/>
};

export const riskLevelBodyTemplate = (rowData: any) => {
    const riskLevel = rowData.vessel.riskLevel ?? 0;
    let riskString = t("riskLevel.none");

    switch (riskLevel) {
        case RiskLevel.very_low:
            riskString = t("riskLevel.veryLow");
            break;
        case RiskLevel.low:
            riskString = t("riskLevel.low");
            break;
        case RiskLevel.medium:
            riskString = t("riskLevel.medium");
            break;
        case RiskLevel.high:
            riskString = t("riskLevel.high");
            break;
        case RiskLevel.very_high:
            riskString = t("riskLevel.veryHigh");
            break;
    }

    return (
        <p>{riskString}</p>
    );
}

export const generateTextBodyTemplate = (fieldName: string, placeholderLocale: string) => {
    return (rowData: any) => {
        let text = rowData[fieldName] ?? "";

        if (typeof text === "number") {
            text = text.toLocaleString();
        }

        if (text.trim().length === 0){
            return <p className="p-inputtext line-height-2 p-component ml-1 w-full defect-note">{t(placeholderLocale)}</p>
        } else {
            return <p className="line-height-2 p-component ml-1 w-full defect-note">{text}</p>
        }
    }
}

export const idwalGradeBody = (rowData: any) => {
    const grade = rowData.inspection?.latestGrades?.idwalGrade ?? rowData.inspection?.idwalGrade ?? rowData.idwalGrade;
    const guid = rowData.inspection?.latestGrades?.inspectionId ?? rowData.inspectionGuid;
    const imo = rowData.vessel?.imo ?? rowData.imo;

    if (grade === undefined || grade === null) {
        return genericGradeTemplate(undefined, imo);
    }
    
    const tooltipContent = `${t("menuButton.viewReport")}`;

    const href = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/${CUSTOMER_PORTAL_ROUTE}/${guid}`;

    return genericGradeTemplate(grade, `idwal-${imo}`, href, tooltipContent, process.env.REACT_APP_OPEN_IN_WINDOW == 'true');
}

export const historicIdwalGradeBody = (rowData: any) => {
    const { id, idwalGrade } = rowData;
    const href = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/${CUSTOMER_PORTAL_ROUTE}/${id}`;

    if (idwalGrade === undefined || idwalGrade === null) {
        return genericGradeTemplate(undefined, id);
    }
    
    return genericGradeTemplate(idwalGrade, id, href, t("fleetTable.viewReport"));
}

export const aiGradeBody = (rowData: any) => {
    const grade = rowData.aiGrades?.grade ? rowData.aiGrades?.grade : undefined;
    const imo = rowData.vessel?.imo ?? rowData.imo;
    const key = `screening-${rowData.vessel?.imo ?? rowData.imo}`;

    if (grade === undefined) {
        return genericGradeTemplate(grade, key);
    }

    const tooltipContent = (
        <div className="flex flex-column">
            <span>{`${rowData.aiGrades?.gradeTypeDisplay}. ${rowData.aiGrades?.gradeDateDisplay}`}</span>
            <span>{t("fleetTable.viewScreeningReport")}</span>
        </div>
    );
    const href = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/${SCREENING_REPORT_ROUTE}/${imo}`;

    return genericGradeTemplate(grade, key, href, tooltipContent);
}


export const noteBodyTemplate = (rowData: any) => {
    const notes = rowData.vessel.notes ?? "";

    if(notes.trim().length === 0){
        return <p className="p-inputtext line-height-2 p-component ml-1 w-full defect-note">{t('defectsTable.notesPlaceholder')}</p>
    } else {
        return <p className="line-height-2 p-component ml-1 w-full defect-note">{notes}</p>
    }
}

export const vesselTypeBodyTemplate = (rowData: any) => {
    let type;
    let subtype;

    if (rowData.type) {
        type = rowData.type;
        subtype = rowData.subType;
    }
    else {
        type = rowData.vessel.type;
        subtype = rowData.vessel.subtype;
    }
   
    return (
        <div>
            <p>{type}</p>
            <small>{subtype}</small>
        </div>
    );
};

export const idwalTrendingTemplate = (rowData: FleetDataItem) => {
    let arrow = rowData.trending != null && rowData.trending <= 0 ? "⬇" : "⬆";
    let trendingValue =  rowData.trending;
    let arrowClass = "trending-down-arrow"
    let trendingString = "";

    if (trendingValue == null) {
        return <></>;
    }

    trendingString = trendingValue.toString();

    if (trendingValue == 0) {
        arrow = "⮕"
        arrowClass = "trending-arrow-equal"
    }

    if (trendingValue != null && trendingValue > 0) {
        arrowClass = "trending-up-arrow"
        trendingString = "+" + trendingValue
    }

    const date = rowData.trendingLastInspected ? new Date(rowData.trendingLastInspected) : new Date();
    const targetClass = "target_" + rowData.vessel.imo;

    return <div className="flex justify-content-center">
        <Tooltip className={"trending-tool-tip"}  autoHide={false} target={`.${targetClass}`} data-cy={targetClass}>
            {t("fleetTable.previouslyInspected")} {date.toLocaleDateString()}
        </Tooltip>

        <div>
            <div className={targetClass}>
                <span className={arrowClass}>{arrow}</span>
                <span className={"trending-text"}>{trendingString} </span>
            </div>
        </div>
    </div>
}

export const actionBodyTemplate = (rowData: any, requestInspectionCallback : any) => {
    const status = rowData.inspection?.status ?? rowData.inspectionStatus;
    const guid = rowData.inspection?.guid ?? rowData.inspectionGuid;
    const idwalGrade = rowData.inspection?.idwalGrade ?? rowData.idwalGrade;
    const grades = getVesselGrade(idwalGrade, status?.toString());
    const reportAvailable = grades && guid && status === INSPECTION_STATUS_FULL_REPORT;
    const reportUrl = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/${CUSTOMER_PORTAL_ROUTE}/${guid}`

    const menuItems = [
        {
            label: t("menuButton.requestInspection"),
            icon: <img className="pr-2" alt="request_inspection" src="./request-inspection-menu.svg"></img>,
            command: () => {
                requestInspectionCallback(rowData);
            }
        }
    ];

    if(reportAvailable) {
        menuItems.push({
            label: t("menuButton.viewReport"),
                icon: <img className="pr-2" alt="view_report" src="./view-report-menu.svg"></img>,
            command: () => openUrl(reportUrl)
            }
        )
    }

    return <MenuButton menu={menuItems}/>
}

export const expansionActionTemplate = (rowData: any) => {
    const guid = rowData.id;

    const reportUrl = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/${CUSTOMER_PORTAL_ROUTE}/${guid}`;
    const onClick = (e: any) => {
        e.preventDefault();
        openUrl(reportUrl);
    }

    return (
        <div className="flex justify-content-start">
            <Button 
                style={{width: "50px"}}
                size={"small"} 
                onClick={(e) => onClick(e)}
                label={t("fleetTable.rowExpansion.viewReport")}
            />
        </div>
    )
}

export const subGradeActionBodyTemplate = (rowData: any) => {
    const guid = rowData.inspection?.guid ?? rowData.inspectionGuid;
    const reportUrl = `${process.env.REACT_APP_ID_PLATFORM_CUSTOMER_REPORT_URL_ROOT}/${CUSTOMER_PORTAL_ROUTE}/${guid}`

    const menuItems = [
        {
            label: t("menuButton.viewReport"),
            icon: <img className="pr-2" alt="view_report" src="./view-report-menu.svg"></img>,
            command: () => openUrl(reportUrl)
        }
    ];
    return <MenuButton menu={menuItems}/>
}


/**
 * Shows status tag in the status dropdown value
 * @param option 
 * @param props 
 * @returns 
 */
export const defectStatusDropdownValueTemplate = (option: any, props: any) => {
    if (props.value !== null && props.value !== undefined) {
        if (props.value === DefectStatus.closed) {
            return <Tag severity="success" value={t("defectsTable.defectStatusClosed")}/>
        }

        if (props.value === DefectStatus.inProgress) {
            return <Tag severity="info" value={t("defectsTable.defectStatusInProgress")}/>
        }

        if (props.value === DefectStatus.rectified) {
            return <Tag severity="warning" value={t("defectsTable.defectStatusRectified")}/>
        }

        if (props.value === DefectStatus.open) {
            return <Tag severity="danger" value={t("defectsTable.defectStatusOpen")}/>
        }
    }  

    return <Tag severity="danger" value={t("defectsTable.defectStatusOpen")}/>
};

/**
 * Shows status tag in the status dropdown option row
 * @param option 
 * @returns
 */
export const defectStatusDropdownOptionTemplate = (option: any) => {
    if (option.code === DefectStatus.closed) {
        return <Tag severity="success" value={t("defectsTable.defectStatusClosed")}/>
    }

    if (option.code === DefectStatus.inProgress) {
        return <Tag severity="info" value={t("defectsTable.defectStatusInProgress")}/>
    }

    if (option.code === DefectStatus.rectified) {
        return <Tag severity="warning" value={t("defectsTable.defectStatusRectified")}/>
    }

    if (option.code === DefectStatus.open) {
        return <Tag severity="danger" value={t("defectsTable.defectStatusOpen")}/>
    }
};

export const vesselInspectionStatusTemplate = (rowData: any) => {
    const statusText = getFriendlyInspectionStatusText(rowData.vesselLatestInspectionStatus);
    return <span>{statusText}</span>;
}

export const lastInspectedTemplate = (rowData: any) => {
    const date = new Date(rowData.inspection?.lastInspected);

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    const formattedDate = `${day}/${month}/${year}`;

    if (isNaN(year)) {
        return <span></span>
    }

    return <span>{formattedDate}</span>
}

export const historicStatusTemplate = (rowData: any) => {
    const statusText = getFriendlyInspectionStatusText(rowData.status);
    return <span>{statusText}</span>;
}

export const historicLocationTemplate = (rowData: any) => {
    const location = rowData.location?.name ?? "";
    const country = rowData.location?.country?.name ?? "";
    return <span>{location}, {country}</span>;
}

export const rowExpansionTemplate = (rowData: any) => {
    // only show the latest inspection of each type in the expansion
    const scoreHistory = filterScoreHistoryByType(rowData.scoreHistory);

    if (!scoreHistory.length) {
        return (
            <div className="fleet-table-row-expansion-container">
                <p className="ml-5">{t("fleetTable.rowExpansion.noInspections")}</p>
            </div>
        );
    }

    return (
        <div className="fleet-table-row-expansion-container">
            <div className="row-expansion-content">
                <DataTable
                    value={scoreHistory}
                    data-cy="fleet-table-row-expansion"
                    scrollable={false}
                    stripedRows={false}
                    style={{ width: "100%" }}
                >
                    <Column style={{ width: "7%", justifyContent: "start" }} field="inspectionType" header={t("fleetTable.inspectionType")} />
                    <Column style={{ width: "3.4%" }} body={expansionActionTemplate} field="expansionActions" header={t("fleetTable.rowExpansion.action")} />
                    <Column style={{ width: "5%", justifyContent: "left" }} headerClassName="score-header" field="idwalGrade" body={historicIdwalGradeBody} header={t("fleetTable.idwalGrade")} />
                    <Column style={{ width: "4%", justifyContent: "center", textAlign: "left"}} field="inspectionNumber" header={t("fleetTable.rowExpansion.inspectionReference")} />
                    <Column style={{ width: "5%" }} field="lastInspected" header={t("fleetTable.rowExpansion.inspectionDate")} body={(e: any) => e.lastInspected ? new Date(e.lastInspected).toLocaleDateString() : ""} />
                    <Column style={{ width: "5%" }}  field="status" header={t("fleetTable.rowExpansion.status")} body={historicStatusTemplate} />
                    <Column style={{ width: "10%" }} header={t("fleetTable.rowExpansion.inspectionLocation")} body={historicLocationTemplate} />
                    <Column style={{ width: "5%", textAlign: "center"}} headerClassName="score-header" field="conditionScore" header={t("fleetTable.conditionGrade")} />
                    <Column style={{ textAlign: "center", width: "5%"}} headerClassName="score-header" field="managementScore" header={t("fleetTable.managementGrade")} />
                    <Column style={{ width: "52%" }} />
                </DataTable>
            </div>
        </div>
    )
}